/**
 * Perfil de instalacao do dispositivo (admin x cliente).
 *
 * O app do Agiliza e um unico PWA com `scope: "/"`. Sem um marcador explicito
 * de "quem instalou", o brand-group guard inferia o perfil a partir da
 * primeira navegacao standalone para uma pagina de marca — o que confundia um
 * administrador visitando a area do cliente com um cliente que instalou o app
 * da marca. O resultado era um app travado: `/admin` e `/login` passavam a
 * redirecionar para `/{slug}` para sempre, porque nada apagava o slug gravado.
 *
 * Este modulo guarda esse estado e a deteccao de sessao de admin. Ele nao
 * importa nada de proposito: `lib/auth.ts` e `lib/brandGroupGuard.ts` dependem
 * dele, e qualquer import aqui criaria um ciclo.
 *
 * O bloqueio continua sendo uma superficie de UX/seguranca, nunca autorizacao:
 * a autorizacao real e do servidor e permanece inalterada.
 */

export type InstallProfile = "admin" | "customer";

/** Perfil gravado para o dispositivo. Sobrevive ao logout de proposito. */
export const INSTALL_PROFILE_KEY = "agiliza-install-profile";

/** Slug de marca a partir da qual o cliente instalou o app. */
export const INSTALLING_SLUG_KEY = "agiliza-installing-slug";

/** Prefixo do cache de slugs irmaos, em sessionStorage. */
export const BRAND_GROUP_CACHE_PREFIX = "agiliza-brand-group-slugs:";

/** Marca que o guard barrou uma navegacao nesta sessao. */
export const BRAND_GUARD_BLOCKED_KEY = "agiliza-brand-guard-blocked";

/** Evento disparado no mesmo instante em que o guard barra uma navegacao. */
export const BRAND_GUARD_BLOCKED_EVENT = "agiliza:brand-guard-blocked";

/** Saida de emergencia: rota de login sempre liberada pelo guard. */
export const ADMIN_ESCAPE_PARAM = "acesso";
export const ADMIN_ESCAPE_VALUE = "estabelecimento";
export const ADMIN_ESCAPE_PATH = `/login?${ADMIN_ESCAPE_PARAM}=${ADMIN_ESCAPE_VALUE}`;

// ---------------------------------------------------------------------------
// Perfil de instalacao
// ---------------------------------------------------------------------------

export function getInstallProfile(): InstallProfile | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(INSTALL_PROFILE_KEY);
    return value === "admin" || value === "customer" ? value : null;
  } catch {
    return null;
  }
}

export function setInstallProfile(profile: InstallProfile): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(INSTALL_PROFILE_KEY, profile);
  } catch {
    // storage indisponivel (modo privado/quota) — ignora silenciosamente.
  }
}

/**
 * Existe token de admin neste dispositivo?
 *
 * Le o localStorage direto para manter este modulo sem imports. `saveToken` e
 * `clearToken` (lib/auth) gravam e apagam localStorage e cookie juntos, entao
 * as duas fontes nao divergem.
 */
export function hasAdminSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.localStorage.getItem("auth_token"));
  } catch {
    return false;
  }
}

/**
 * Dispositivo de administrador: ja entrou como estabelecimento alguma vez, ou
 * tem sessao de admin ativa agora. Nunca deve ser travado no app do cliente.
 */
export function isAdminDevice(): boolean {
  return getInstallProfile() === "admin" || hasAdminSession();
}

// ---------------------------------------------------------------------------
// Slug de instalacao
// ---------------------------------------------------------------------------

export function getInstallingSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(INSTALLING_SLUG_KEY) || null;
  } catch {
    return null;
  }
}

export function setInstallingSlug(slug: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(INSTALLING_SLUG_KEY, slug);
  } catch {
    // storage indisponivel (modo privado/quota) — ignora silenciosamente.
  }
}

// ---------------------------------------------------------------------------
// Destravamento
// ---------------------------------------------------------------------------

/**
 * Apaga o bloqueio de marca: slug de instalacao, cache de slugs irmaos, aviso
 * de navegacao barrada e o perfil `customer` (o perfil `admin` permanece).
 */
export function clearBrandGroupLock(): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(INSTALLING_SLUG_KEY);
    if (getInstallProfile() === "customer") {
      window.localStorage.removeItem(INSTALL_PROFILE_KEY);
    }
  } catch {
    // ignora falha de storage.
  }

  try {
    const stale: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(BRAND_GROUP_CACHE_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => window.sessionStorage.removeItem(key));
    window.sessionStorage.removeItem(BRAND_GUARD_BLOCKED_KEY);
  } catch {
    // ignora falha de storage.
  }
}

/**
 * Marca o dispositivo como de administrador e destrava o app.
 *
 * Chamado ao salvar um token de admin (login, cadastro, impersonacao) e pela
 * saida de emergencia. O perfil e mantido apos o logout: e exatamente isso que
 * impede o app de voltar a prender o administrador na area do cliente.
 */
export function markAdminDevice(): void {
  if (typeof window === "undefined") return;
  setInstallProfile("admin");
  clearBrandGroupLock();
}

// ---------------------------------------------------------------------------
// Aviso de navegacao barrada
// ---------------------------------------------------------------------------

export function markBrandGuardBlocked(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(BRAND_GUARD_BLOCKED_KEY, "1");
  } catch {
    // ignora falha de storage; o evento abaixo ainda avisa a UI montada.
  }
  try {
    window.dispatchEvent(new Event(BRAND_GUARD_BLOCKED_EVENT));
  } catch {
    // ambientes sem Event construtor — ignora.
  }
}

export function isBrandGuardBlocked(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(BRAND_GUARD_BLOCKED_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearBrandGuardBlocked(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(BRAND_GUARD_BLOCKED_KEY);
  } catch {
    // ignora falha de storage.
  }
}
